import React, { useEffect, useCallback, useState, useMemo } from 'react';
import LayoutWithMembershipGuard from './components/Layout/LayoutWithMembershipGuard';
import {
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import Layout from './components/Layout/Layout';
import _, { isArray, isEmpty } from 'lodash';
import { Spin } from 'antd';
import { ConfigProvider } from 'antd';
import useCssVariables from './hooks/useCssVariables';
import {
  setIsOtpAuthCheckModalOpen,
  updateConfig,
} from './store/config';
import { useDispatch, useSelector } from 'react-redux';
import he from 'antd/es/locale/he_IL';
import en from 'antd/es/locale/en_US';
import ru from 'antd/es/locale/ru_RU';
import uk from 'antd/es/locale/uk_UA';
import fr from 'antd/es/locale/fr_FR';
import es from 'antd/es/locale/es_ES';
import pt from 'antd/es/locale/pt_PT';
import ja from 'antd/es/locale/ja_JP';
import ko from 'antd/es/locale/ko_KR';
import { RootState } from './store/store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import moment plugin to register formatTZ method globally
import '@skygroup/shared/utils/momentTzPlugin';

import './assets/fonts/Heebo/heebo.css';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import useSiteLang from './hooks/useSiteLang';
import useAppConfig from './hooks/useAppConfig';
import SiteNotAvailable from './components/SiteNotAvailable/SiteNotAvailable';
import useRatesMidnightRefresh from './hooks/useRatesMidnightRefresh';
import { useLoadAgentUser } from './hooks/useLoadAgentUser';
import { useLoadOrderAttachContext } from './hooks/useLoadOrderAttachContext';
import OrderAttachConfirmModal from './components/OrderAttachBanner/OrderAttachConfirmModal';
import ShoppingCartV2 from './modules/ShoppingCart/ShoppingCartV2';
import BotWidgetGate from './components/bot/BotWidgetGate';
import AcceptCookies from './components/AcceptCookies/AcceptCookies';
import SiteEngineModuleTour from './components/SiteEngineModuleTour/SiteEngineModuleTour';
import AccessibilityPlugin from './utils/definedScripts/accessibilityPlugin';
import { FaArrowUp } from 'react-icons/fa';
import useMembershipAuthHelpers from './hooks/useMembershipAuthHelpers';
import useHandleMessagesFromBO from './useHandleMessagesFromBO';
import ErrorThrower from '@skygroup/shared/components/ErrorBoundary/ErrorThrower';
import DynamicPageEdit from './components/DynamicPages/dynamicPageEdit';
import ScrollToTop from './utils/ScrollToTop';
import { DynamicPageType } from './interfaces';
import useMembershipInit from './hooks/useMembershipInit';
import ChangeApprovalPage from './modules/ChangeApprovalPage/ChangeApprovalPage';
import ChangeApprovalLayout from './modules/ChangeApprovalPage/ChangeApprovalLayout';
import SupplierApprovalPage from './modules/SupplierApprovalPage/SupplierApprovalPage';
import SupplierApprovalLayout from './modules/SupplierApprovalPage/SupplierApprovalLayout';
import VideoNotificationsProvider from '@skygroup/shared/components/must-watch-video/VideoNotificationsContext';
import useSheetSwipe from '@skygroup/shared/components/mobile-sheet-gestures/useSheetSwipe';
import { B2C_SHEET_SWIPE_SPECS } from './utils/sheetSwipeSpecs';
import MobileSelectSheetLayer from './components/MobileSelectSheet/MobileSelectSheetLayer';
import useKeyboardAwareSheets from '@skygroup/shared/components/mobile-keyboard/useKeyboardAwareSheets';
import {
  buildEngagementTranslations,
  EngagementTranslationsProvider,
} from '@skygroup/shared/components/engagement';
import useMustWatchVideoNotification from './hooks/useMustWatchVideoNotification';
import { isMobileViewportWidth } from './utils/breakpoints';

const ANT_LOCALES = {
  en: en,
  he: he,
  ru: ru,
  uk: uk,
  fr: fr,
  es: es,
  pt: pt,
  ja: ja,
  ko: ko,
};

type DynamicRoutesTypes = {
  key: string;
  path: string;
  type?: DynamicPageType;
  isEdit?: boolean;
};

const dynamicRoutes: DynamicRoutesTypes[] = [
  {
    key: 'dynamicPagetype1',
    path: 'prm/:secret',
    type: DynamicPageType.PROMOTION,
  },
  {
    key: 'dynamicPagetype2',
    path: 'dst/:secret',
    type: DynamicPageType.DESTINATION,
  },
  {
    key: 'dynamicPagetype3',
    path: 'page/:secret',
    type: DynamicPageType.GENERAL,
  },
  {
    key: 'dynamicPageEdit',
    path: 'dynamicPageEdit',
    isEdit: true,
  },
];

const queryClient = new QueryClient();

// Renders below App's QueryClientProvider so the must-watch query has a client
const MustWatchVideoGate = ({ children }: { children: React.ReactNode }) => {
  const engagementSource = useSelector((state: RootState) => (state.config.translations as any)?.engagement);
  const engagementTranslations = useMemo(
    () => buildEngagementTranslations(engagementSource),
    [engagementSource]
  );
  const {
    notifications: videoNotifications,
    translations: mustWatchTranslations,
    handleDone: handleMustWatchDone,
    enabled: isMustWatchEnabled,
  } = useMustWatchVideoNotification();

  return (
    <EngagementTranslationsProvider t={engagementTranslations}>
      <VideoNotificationsProvider
        notifications={videoNotifications}
        enabled={isMustWatchEnabled}
        t={mustWatchTranslations}
        onDone={handleMustWatchDone}
      >
        {children}
      </VideoNotificationsProvider>
    </EngagementTranslationsProvider>
  );
};

function App() {
  const dispatch = useDispatch();
  const search = useSelector((state: RootState) => state.search);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAgentLoading } = useLoadAgentUser();
  // Consume add/replace-product-on-existing-order context from Backoffice
  const {
    showAttachConfirm,
    acknowledgeAttach,
    cancelAttachToOrder,
    pendingAttachContext,
  } = useLoadOrderAttachContext();
  const {
    config,
    agentInfo,
    currentLang,
    translations: t,
  } = useSelector((data: RootState) => data.config);
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => !isMobileViewportWidth(window.innerWidth)
  );

  const isPreviewMode = search?.searchParams?.prvmd === '1';
  const isEditMode = searchParams.get('editMode') === 'true';

  // Load app configuration
  const { isConfigLoaded, configError, blockedSite } = useAppConfig();

  const { isAuthenticated } = useMembershipAuthHelpers();

  const { isTranLoaded, direction } = useSiteLang({
    siteConfig: config?.siteConfig,
  });

  // init membership
  useMembershipInit({
    isTranLoaded,
    isConfigLoaded,
    isAuthenticated,
  });

  useEffect(() => {
    try {
      const clickId = searchParams.get('click_id');
      if (clickId) {
        sessionStorage.setItem('click_id', clickId);
      }
    } catch (e) {
      // Fail silently if sessionStorage is unavailable
      console.error('Failed to store click_id in sessionStorage', e);
    }
  }, [searchParams]);

  const { selectedInstance, setSelectedInstance } = useHandleMessagesFromBO();

  // Exchange rates roll over at the browser's local midnight; without this a
  // tab left open overnight keeps quoting yesterday's, cart included.
  useRatesMidnightRefresh({ isConfigLoaded, isEditMode, isPreviewMode });

  // Phones: swipe any bottom sheet down to close it.
  useSheetSwipe(B2C_SHEET_SWIPE_SPECS);

  useEffect(() => {
    if (
      isConfigLoaded &&
      config?.siteConfig?.membership?.auth_type === 'org_istadrut' &&
      !isAuthenticated &&
      location.pathname !== '/order/manage'
    ) {
      dispatch(setIsOtpAuthCheckModalOpen(true));
    }
  }, [isConfigLoaded, config, isAuthenticated, location, dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 6000) {
        setShowBackToTopButton(true);
      } else {
        setShowBackToTopButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Phones: sheets stay above the on-screen keyboard, the focused field in view.
  useKeyboardAwareSheets(!isDesktop);

  useEffect(() => {
    const onResize = () =>
      setIsDesktop(!isMobileViewportWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const generateCssVariables = useCssVariables();

  let topClass = config?.siteConfig?.style?.topClass
      ? config?.siteConfig?.style?.topClass
      : '';

  if (agentInfo?.agentMode) {
    topClass += ' agent-mode';
  }

  const antLocale = useMemo(() => {
    if (!currentLang || !currentLang?.split('_')[0]) {
      return he;
    }
    return ANT_LOCALES[currentLang?.split('_')[0] as keyof typeof ANT_LOCALES];
  }, [currentLang]);

  // the agency of this site is not active / not paying - nothing else of the site loads
  if (blockedSite) {
    return <SiteNotAvailable info={blockedSite} />;
  }

  return (
      <ErrorThrower error={configError}>
        <SpinLoader
            isLoading={!(isConfigLoaded && isTranLoaded) || isAgentLoading}
        >
          <QueryClientProvider client={queryClient}>
            <div
                className={`App direction-${direction} ${topClass}`}
                id="App"
                dir={direction}
            >
              {config?.siteConfig?.cssStyle ? <style
                  dangerouslySetInnerHTML={{
                    __html: config?.siteConfig?.cssStyle,
                  }}
              /> : null}
              <ScrollToTop />
              {generateCssVariables('body', config?.theme)}
              {!(
                  isPreviewMode ||
                  isEditMode ||
                  location.pathname.split('/')[1] === 'checkout' ||
                  location.pathname === '/checkout' ||
                  location.pathname.split('/')[1] === 'order' ||
                  location.pathname === '/order' ||
                  location.pathname.split('/')[1] === 'supplier-approval'
              ) && <ShoppingCartV2 pageType="rest" />}

              {!isPreviewMode && !isEditMode &&
                location.pathname.split('/')[1] !== 'supplier-approval' && (
                <BotWidgetGate isDesktop={isDesktop} />
              )}

              {showBackToTopButton && (
                  <div
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="back-to-top"
                  >
                    <FaArrowUp />
                  </div>
              )}

              <ConfigProvider direction={direction} locale={antLocale || he}>
                {/* Video notification gate — while an agent in agent mode has
                    an unseen must-watch video, the whole site is locked except
                    the notification bell in the agent banner. */}
                <MustWatchVideoGate>
                <Routes>
                      <Route
                        key={"changeApproval"}
                        path={"/change-approval/:uuid"}
                        element={
                          <ChangeApprovalLayout>
                            <ChangeApprovalPage />
                          </ChangeApprovalLayout>
                        }
                      />
                      <Route
                        key={"supplierApproval"}
                        path={"/supplier-approval"}
                        element={
                          <SupplierApprovalLayout>
                            <SupplierApprovalPage />
                          </SupplierApprovalLayout>
                        }
                      />
                  {dynamicRoutes.map((route) => (
                      <Route
                          key={route.key}
                          path={route.path}
                          element={
                            <DynamicPageEdit
                                isPreviewMode={isPreviewMode}
                                selectedInstance={selectedInstance}
                                setSelectedInstance={setSelectedInstance}
                                isEdit={route.isEdit}
                                type={route.type}
                            />
                          }
                      />
                  ))}

                  {config?.pages?.map((page, i) => {
                    const pathArray: string[] = isArray(page.path)
                        ? page.path
                        : [page.path];
                    return pathArray.map((path, index) => {
                      return (
                        <Route
                        key={index + path}
                        path={page.name === 'NotFound404' ? '*' : path}
                        element={
                          <LayoutWithMembershipGuard
                            // Force remount when page identity changes so React
                            // does not reuse Layout state across /events/:id →
                            // /checkout/:id (same component type, different modules).
                            key={String(page.id || page.name || path)}
                            pageId={page.id || page.name}
                            pagePath={path}
                            pageComponents={page.modules}
                            layout={page.layout}
                            isPreviewMode={isPreviewMode}
                            selectedInstance={selectedInstance}
                            setSelectedInstance={setSelectedInstance}
                            page={page}
                          />
                        }
                      />
                      );
                    });
                  })}
                </Routes>
                </MustWatchVideoGate>
                <OrderAttachConfirmModal
                  open={showAttachConfirm}
                  onContinue={acknowledgeAttach}
                  onCancelMode={cancelAttachToOrder}
                  context={pendingAttachContext}
                />
                {isEmpty(config) && <Spin className="center" size="large" />}
              </ConfigProvider>
            </div>
            {!isEditMode && config?.siteConfig?.accessibility?.siteKey ? (
                <AccessibilityPlugin
                    siteKey={config?.siteConfig?.accessibility?.siteKey}
                />
            ) : null}
            {/* phones: selects as bottom sheets, a tap never focuses the field */}
            <MobileSelectSheetLayer />
            <AcceptCookies />
            {/* Site-engine module-actions tour — only mounts inside the
                preview iframe (editMode) and self-gates on the &seTour=1
                flag the Backoffice adds while its site-engine tour runs. */}
            {isEditMode && <SiteEngineModuleTour />}
          </QueryClientProvider>
        </SpinLoader>
      </ErrorThrower>
  );
}

export default App;
